import type { FindFn } from "@shared/models/analytics/base"
import { createBlankEvent } from "@shared/models/analytics/base"
import type { LessonEditCard, LessonEditPaste } from "@shared/models/analytics/lesson"
import { getPossessiveForLesson } from "@shared/models/analytics/lesson"
import { getPossessiveForPlanbook } from "@shared/models/analytics/planbook"
import type { CardStackAttributes, ParentCardStack } from "@shared/models/card-stack"
import type { Course } from "@shared/models/course"
import type { PatchModel } from "@shared/models/patch"
import Patch from "@shared/models/patch-updater"
import type { Planbook } from "@shared/models/planbook"
import { compact, partial } from "lodash-es"
import type { PreparedAction } from "../../action-doc-builder"
import type { Narrative } from "../create-narrative"
import defaultLessonActivity from "./default-lesson-activity"
import defaultLessonContext from "./default-lesson-context"
import defaultLessonModels from "./default-lesson-models"

interface Payload {
  cardStackId: string
  course: Course
  planbook: Planbook
  newLessonAttributes: CardStackAttributes
  originalLessonAttributes: CardStackAttributes
  copiedFrom: string
  parentCardStackInfo: ParentCardStack
}

export function preparePasteLesson(payload: Payload): PreparedAction {
  return {
    name: LESSON_PASTE.name,
    payload: payload,
    patches: LESSON_PASTE.patches(payload),
    undoPatches: LESSON_PASTE.undoPatches(payload),
    narrative: partial(LESSON_PASTE.narrative, payload),
  }
}

export const LESSON_PASTE = {
  name: "LESSON_PASTE",

  params: {
    cardStackId: "string",
    newLessonAttributes: "object",
    originalLessonAttributes: "object",
    copiedFrom: "string",
  },

  // The card ids (and, for unified card stacks, the proseMirror card/character ids) are
  // remapped by the caller via cloneCardStackAttributesWithNewIds, so newLessonAttributes
  // is ready to be copied onto the target card stack as-is.
  patches(payload: Payload): Array<PatchModel> {
    const patches: PatchModel[] = []

    let newAttrs = payload.newLessonAttributes
    let patch = Patch.create("card-stack", payload.cardStackId)
    patch.set(`attributes.schemaVersion`, newAttrs.schemaVersion)
    patch.set(`attributes.cards`, newAttrs.cards)
    patch.set(`attributes.title`, newAttrs.title)
    patch.set("attributes.parentCardStack", payload.parentCardStackInfo)
    if (newAttrs.schemaVersion === 2) {
      patch.set("attributes.proseMirror", newAttrs.proseMirror)
      patch.set("attributes.extracted", newAttrs.extracted)
    }
    patches.push(patch.patch)

    let patchForCardStackSummary = Patch.create("card-stack-summary", payload.cardStackId)
    patchForCardStackSummary.set("attributes.title", payload.newLessonAttributes.title)
    patches.push(patchForCardStackSummary.patch)

    return patches
  },

  undoPatches(payload: Payload): Array<PatchModel> {
    const undoPatches: PatchModel[] = []

    let originalLessonAttributes = payload.originalLessonAttributes || {
      cards: null,
      title: "",
    }
    let patch = Patch.create("card-stack", payload.cardStackId)
    patch.set(`attributes.schemaVersion`, originalLessonAttributes.schemaVersion)
    patch.set(`attributes.cards`, originalLessonAttributes.cards)
    patch.set(`attributes.title`, originalLessonAttributes.title)
    patch.set(`attributes.parentCardStack`, originalLessonAttributes.parentCardStack)
    if (originalLessonAttributes.schemaVersion === 2) {
      patch.set("attributes.proseMirror", originalLessonAttributes.proseMirror)
      patch.set("attributes.extracted", originalLessonAttributes.extracted)
    }
    undoPatches.push(patch.patch)

    let patchForCardStackSummary = Patch.create("card-stack-summary", payload.cardStackId)
    patchForCardStackSummary.set("attributes.title", originalLessonAttributes.title)
    undoPatches.push(patchForCardStackSummary.patch)

    return undoPatches
  },

  narrative(payload: Payload, findFn: FindFn, userId: string): Narrative | null {
    let models = defaultLessonModels(payload, findFn, userId)
    let context = defaultLessonContext(payload, models)
    let donorCardStackTitle = models.cardStack ? models.cardStack.attributes.title : null
    let recipientLessonTitle = context.cardStackTitle
    let title = "Pasted a Lesson"
    let titleForAnalytics = "Lesson - Paste"
    let description = `Pasted ${donorCardStackTitle} over ${recipientLessonTitle}`

    context.donorCardStackTitle = donorCardStackTitle
    context.donorCardStackId = payload.parentCardStackInfo.id
    context.editType = "paste"

    let sharedEventProperties: Pick<
      LessonEditPaste,
      | "root_id"
      | "root_type"
      | "date_plan_scheduled_for"
      | "primary_object_id"
      | "primary_object_id_and_ancestor_ids"
      | "primary_verb"
      | "primary_possessive"
      | "primary_object_type"
      | "adverbial_possessive"
      | "adverbial_verb"
      | "adverbial_object_id"
      | "adverbial_object_id_and_ancestor_ids"
    > = {
      root_id: models.planbook?.id ?? null,
      root_type: "planbook",
      date_plan_scheduled_for: models.dateStringForCardStack,
      primary_object_id: payload.cardStackId,
      primary_object_id_and_ancestor_ids: models.cardStackIdAndAncestorIds,
      primary_verb: "edit",
      primary_possessive: getPossessiveForPlanbook(findFn, userId, models.planbook?.id),
      primary_object_type: "lesson",
      adverbial_possessive: getPossessiveForLesson(
        findFn,
        userId,
        payload.parentCardStackInfo?.ownerIds || [],
      ),
      adverbial_verb: "pasting",
      adverbial_object_id: payload.parentCardStackInfo.id,
      adverbial_object_id_and_ancestor_ids: compact([
        payload.parentCardStackInfo.planbookId,
        payload.parentCardStackInfo.courseId,
        payload.parentCardStackInfo.id,
      ]),
    }

    let newEvent: LessonEditCard | LessonEditPaste | null = null

    if (payload.copiedFrom === "template") {
      newEvent = {
        ...createBlankEvent(),
        ...sharedEventProperties,

        primary_object_specifier: "cards",
        adverbial_object_type: "template",
        adverbial_object_specifier: null,
      }
      title = "Replaced a Lesson with a Template"
      titleForAnalytics = "Lesson - Replace With Template"
      description = `Replaced ${recipientLessonTitle} with your "${donorCardStackTitle}" template`
    } else {
      newEvent = {
        ...createBlankEvent(),
        ...sharedEventProperties,

        primary_object_specifier: null,
        adverbial_object_type: "lesson",
        adverbial_object_specifier: "single_lesson",
      }
    }

    return {
      title: title,
      titleForAnalytics: titleForAnalytics,
      description: description,
      context: context,
      activity: defaultLessonActivity(payload, models),
      newAnalyticsEvents: [newEvent],
    }
  },
}

export default LESSON_PASTE
